import { Plus, RefreshCcw } from '@styled-icons/feather';
import { SyncDisabled } from '@styled-icons/material';
import { ContainerElement, SyncedContainerElement } from '@typedream/core';
import { getNode, replaceNodeChildren, setNodes } from '@udecode/plate-core';
import { useMachine } from '@xstate/react';
import classNames from 'classnames';
import React from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { assign } from 'xstate/lib/actions';
import { Popup } from '../../../components/Popup/Popup';
import Tooltip from '../../../components/Tooltip';
import { ContainerElementPluginOptions } from '../../../nodes/container';
import { syncedMachine } from '../../../nodes/container/fsm/Synced.fsm';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { ELEMENT_CONTAINER } from '../../../pluginKeys';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import styles from '../SideToolbar.module.css';
import SyncContainerSuggestions from './SyncedContainerSuggestions';

const StyledSmallDescription = styled.div`
  margin: 0.5em 0;
  font-size: 10px;
  line-height: 1.2em;
`;

export function SyncedContainerMenu() {
  const editor = useTPlateEditorRef();

  const [element, path] = useDreamEditorStore(
    () => getSelectedElementEntry<ContainerElement>(editor) || [undefined, undefined],
  );

  const transRefName = element?.name || '';

  const { options } = getTPlugin<ContainerElementPluginOptions>(editor, ELEMENT_CONTAINER);

  const { createTransclusionReference, getTransclusionReferences } = options || {};

  const [machineState, machineSend, machineActor] = useMachine(syncedMachine, {
    context: {
      containerElement: undefined,
      containerElementPath: undefined,
    },
    actions: {
      effectUpdateSlateNodeTransclusionID: (context, event) => {
        setNodes(editor, {
          transclusion_reference_id: event.newTransclusionID,
        }, {
          at: context.containerElementPath,
        });
      },
      createTransclusionReference: (context, event) => {
        const newElement: Partial<SyncedContainerElement> = {
          ...(context.containerElement || {}),
          transclusion_enabled: true,
          transclusion_updated_at: new Date().getTime(),
          transclusion_reference_id: event.newTransclusionID,
          name: `SyncedBlock-${event.newTransclusionID.slice(0, 2)}`,
          children: context.containerElement?.children || [],
        };

        createTransclusionReference?.(event.newTransclusionID, newElement);

        setNodes(editor, newElement, {
          at: context.containerElementPath,
        });
      },
      effectReplaceSlateNodeWithTransclusionContent: (context, event) => {
        if (!getTransclusionReferences) return;

        getTransclusionReferences().then((refs) => {
          setNodes(editor, refs[event.transclusionID], { at: context.containerElementPath });
          replaceNodeChildren(editor, {
            at: context.containerElementPath,
            nodes: refs[event.transclusionID].children,
          });
        });
      },
      effectRemoveSlateNodeTransclusion: (context) => {
        setNodes(editor, {
          transclusion_reference_id: undefined,
          transclusion_enabled: false,
        }, {
          at: context.containerElementPath,
        });
      },
      assignSlateSyncedContainerDataToContext: assign((_context, event) => {
        const node = getNode<ContainerElement>(editor, event.newPath);
        if (!node) {
          return {
            containerElement: undefined,
            containerElementPath: undefined,
          };
        }
        return {
          containerElement: node,
          containerElementPath: event.newPath,
        };
      }),
    },
  });

  React.useEffect(() => {
    if (path) {
      machineSend({ type: 'ON_CHANGE_ELEMENT_PATH', newPath: path });
    }
  }, [element, path]);

  if (!options?.isTransclusionEnabled) return null;

  return (
    <div className={styles.section}>
      <span className={styles.label}>
        Synced Container
      </span>
      {machineState.hasTag('ui:non_transclusion') && (
      <button
        className={styles.button}
        type="button"
        onClick={() => {
          const newID = uuidv4();
          machineSend({ type: 'ON_CREATE_SYNCED_CONTAINER', newTransclusionID: newID });
        }}
      >
        <Plus size={16} className="mr-2" />
        New Synced Container
      </button>
      )}
      {machineState.hasTag('ui:transclusion_enabled') && (
      <>
        <button
          className={styles.button}
          type="button"
          onClick={() => {
            machineSend({ type: 'ON_CREATE_SYNCED_CONTAINER', newTransclusionID: uuidv4() });
          }}
        >
          <RefreshCcw size={16} className="mr-2" />
          New Synced Container
        </button>

        <button
          type="button"
          className={styles.button}
          style={{
            marginTop: '0.5rem',
          }}
          onClick={() => {
            machineSend({ type: 'ON_DETACH_SYNCED_CONTAINER' });
          }}
        >
          Don&apos;t Sync
        </button>
      </>
      )}
      {machineState.hasTag('ui:transclusion_connected') && (
      <>
        <div className={styles.card}>
          <div className="is-flex is-justify-content-space-between is-align-items-center">
            <div>{transRefName}</div>
            <Tooltip center="Detach this container">
              <button
                className={styles.icon}
                type="button"
                onClick={() => {
                  machineSend({ type: 'ON_TRIGGER_DETACH_FLOW' });
                }}
              >
                <SyncDisabled
                  size={16}
                />
              </button>
            </Tooltip>
          </div>
        </div>
        <StyledSmallDescription>
          This is a name to help you see which blocks are synced to the same content.
          <br />
        </StyledSmallDescription>
        <Popup
          height={400}
          width={400}
          open={machineState.hasTag('ui:showing_detach_confirmation')}
          setopen={(open) => {
            if (!open) {
              machineSend({ type: 'ON_CANCEL_DETACH_FLOW' });
            }
          }}
          className="p-6"
        >
          <div className="is-size-6 with-text-color mb-3">
            Detach this container?
          </div>
          <hr />
          <div className="is-subtitle with-light-text-color mb-3">
            This container will no longer be synced with other Synced Containers with the name
            {' '}
            <span className="with-text-color">{transRefName}</span>
          </div>
          <div className="is-subtitle with-light-text-color mb-3">
            This will not affect the other Synced Containers.
          </div>
          <button
            type="button"
            className={classNames(
              'button',
              'is-danger',
              'with-danger-transparent-background-color',
              'with-danger-text-color',
              'has-font-weight-normal',
              'mt-2',
            )}
            onClick={() => machineSend({
              type: 'ON_DETACH_SYNCED_CONTAINER',
            })}
          >
            Detach this container
          </button>
        </Popup>
      </>
      )}

      <SyncContainerSuggestions machine={machineActor} />
    </div>
  );
}
